import { useMemo, useRef, type FC } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment } from 'react-relay';
import { BarHeader } from 'dibs-elements/exports/BarHeader.tsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile.tsx';
import { QuickViewProvider } from 'dibs-buyer-product-tile/exports/QuickView.ts';
import { useSharedUrgencySignals } from '../hooks/sharedUrgencySignals/useSharedUrgencySignals.ts';
import { useItemShippingPrices } from '../hooks/useItemShippingPrices.ts';
import { useSbSharedItemTracking } from '../hooks/sharedItemTracking/useSbSharedItemTracking.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { getIsOpenPdpInNewTabVariant } from '../../utils/abTestHelper.ts';

import { type SbSharedRelatedItems_itemSearch$key } from './__generated__/SbSharedRelatedItems_itemSearch.graphql.ts';
import { type SbSharedRelatedItems_viewer$key } from './__generated__/SbSharedRelatedItems_viewer.graphql.ts';

import styles from './SbSharedRelatedItems.scss';

const title = (
    <FormattedMessage id="sb.SbSharedRelatedItems.title" defaultMessage="Related Items" />
);

const viewerFragment = graphql`
    fragment SbSharedRelatedItems_viewer on Viewer {
        ...SharedItemTile_viewer
        ...useSbSharedItemTracking_viewer
    }
`;
const itemSearchFragment = graphql`
    fragment SbSharedRelatedItems_itemSearch on ItemSearchQueryConnection {
        ...SharedItemTile_itemSearch
        ...useSbSharedItemTracking_itemSearch
        relatedItems: youMayAlsoLike(first: $relatedItemsCount) {
            edges {
                node {
                    item {
                        serviceId
                        ...SharedItemTile_item
                            @arguments(
                                isTrade: $isTrade
                                priceBookName: $priceBookName
                                showSeller: $showSeller
                            )
                        ...useSharedUrgencySignals_item
                        ...useItemShippingPrices_item
                        ...useSbSharedItemTracking_item
                        ...shippingCostHelpers_item
                    }
                }
            }
        }
    }
`;

type Props = {
    viewer: SbSharedRelatedItems_viewer$key;
    itemSearch: SbSharedRelatedItems_itemSearch$key;
};

export const SbSharedRelatedItems: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
}) => {
    const gridContainerRef = useRef<HTMLDivElement | null>(null);
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const { relatedItems } = itemSearch;
    const relatedItemsList = useMemo(
        () => (relatedItems?.edges || []).map(item => item?.node?.item || null).filter(filterFalsy),
        [relatedItems]
    );

    const { fetchItemUrgencySignals, getUrgencySignal } = useSharedUrgencySignals(relatedItemsList);
    const { fetchItemShippingPrices, getItemShippingPrices, getItemShippingTrackingData } =
        useItemShippingPrices(relatedItemsList);
    const { fireItemImpressionTracking, fireItemClickTracking } = useSbSharedItemTracking({
        viewer,
        itemSearch,
        items: relatedItemsList,
        pageType: 'related',
        getUrgencySignal,
        getItemShippingPrices,
        getItemShippingTrackingData,
    });

    const { isQuickFiltersRedesignVariant } = useServerVarsContext();
    const isClient = useClientState();
    const isOpenPdpInNewTabVariant = isClient && getIsOpenPdpInNewTabVariant();

    if (relatedItemsList.length === 0) {
        return null;
    }

    return (
        <>
            <BarHeader title={title} />
            <QuickViewProvider elementRef={gridContainerRef}>
                <div
                    className={classnames(styles.grid, {
                        [styles.isQuickFiltersRedesignVariant]: isQuickFiltersRedesignVariant,
                    })}
                    data-tn="related-items"
                    ref={gridContainerRef}
                >
                    {relatedItemsList.map((relatedItem, index) => (
                        <SharedItemTile
                            user={null}
                            key={index}
                            index={index}
                            isOpenPdpInNewTabVariant={isOpenPdpInNewTabVariant}
                            item={relatedItem}
                            viewer={viewer}
                            itemSearch={itemSearch}
                            beforeRender={() => {
                                fetchItemUrgencySignals(index);
                                fetchItemShippingPrices(index);
                            }}
                            onContentVisible={() => {
                                fireItemImpressionTracking({
                                    itemId: relatedItem.serviceId,
                                    index,
                                });
                            }}
                            onClick={() =>
                                fireItemClickTracking({
                                    itemId: relatedItem.serviceId,
                                    index,
                                })
                            }
                            urgencySignal={getUrgencySignal(relatedItem.serviceId)?.message}
                            shippingPrices={getItemShippingPrices(relatedItem.serviceId)}
                        />
                    ))}
                </div>
            </QuickViewProvider>
        </>
    );
};
